import Input from "../shared/input/input";
import Label from "../shared/label/label";
import Wrapper from "../shared/wrapper/wrapper";
import { FormFieldProps } from "./types/formFieldProps";
import { stylizationWrapper } from "../shared/stylization";
import { inputProps, inputCustomizedProps } from "../shared/input/types/inputProps";
import { labelProps } from "../shared/label/types/labelProps";
import { ReactComponent as emergencyHome } from '../../icons/emergency_home.svg';
import { ReactComponent as cancel } from '../../icons/cancel.svg';
import ErrorTip from "../shared/errorTip/errorTip";
import { TipProps } from "../shared/errorTip/types/errorTipProps";
import { useEffect, useState } from "react";
import { inputStateType, validation } from "../../helpers/validation/validation";
import UseDebounce from "../../helpers/debounce";

const FormField = (props: FormFieldProps) => {

    var stylization = props.style !== undefined ? props.style : stylizationWrapper;

    const [errorCautionDisplay, setErrorCautionDisplay] = useState('none');
    const [pristine, setPristine] = useState(props.pristine);
    const [ValidIcon, setValidIcon] = useState('none');
    const [inputState, setInputState] = useState('');
    const [errorText, setErrorText] = useState(props.tipText);

    const showError = () => {
        setErrorCautionDisplay('inline-block');
        setInputState(inputStateType.inputError);
    }

    const showWarning = () => {
        setErrorCautionDisplay('inline-block');
        setInputState(inputStateType.inputWarning);
    }

    const clearError = () => {
        setErrorCautionDisplay('none');
        setInputState('');
    }

    const showValid = () => {
        setValidIcon('inline-block');
        if (!props.keepPristine) setInputState(inputStateType.inputValid);
    }

    const clearValid = () => {
        setValidIcon('none');
        setInputState('');
    }

    const showIcon = () => {
        setValidIcon('inline-block');
    }

    useEffect(() => {
        let hasWarning = false;
        let hasError = false;
        if (props.validationError === true) {
            var errorText = validation(props.configValidation, props.value);
            hasError = errorText !== "";
            if (!hasError) {
                errorText = validation(props.configValidationWarnings, props.value);
                hasWarning = true;
            }
            if (errorText || (props.tipText && props.tipText !== "")) {
                if (props.tipText && props.tipText !== "") {
                    setErrorText(props.tipText);
                } else {
                    setErrorText(errorText);
                }
                hasWarning ? showWarning() : showError();
                setValidIcon('none');
            } else {
                clearError();
                if (!pristine) showValid();
            }
        } else {
            clearError();
            if (!pristine) showValid();
            if (props.pristineIcon) showIcon();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.validationError, pristine, props.tipText, props.reload])

    const onChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (props.setValue) {
            if((props.withDecimals || props.onlyDigits) && (event.target.value === '-' || event.target.value === '.')){
                event.target.value = "";
                props.setValue("");
            } else {
                props.setValue(event.target.value);
            }
        }
        var errorText = validation(props.configValidation, event.target.value);
        var onlyWarning = false;
        if (errorText === "") {
            errorText = validation(props.configValidationWarnings, event.target.value);
            onlyWarning = true;
        }
        if (errorText) {
            clearValid();
            setErrorText(errorText);
            onlyWarning ? showWarning() : showError();
        }
        else {
            setPristine(false);
            clearError();
            showValid();
            setErrorText('');
        }
    }

    const validateIntegerInputWithNegatives = (input: any) => {
        // Remove non-integer characters
        let value = input.target.value;
        if (value.startsWith('-')) {
            value = '-' + value.slice(1).replace(/[^0-9]/g, '');
        }
        else if (value) {
            value = value.replace(/[^0-9]/g, '');
        }
        if(value && (props.min || props.min === 0) && (props.min as number) > (value as number)){
            value = props.value === undefined ? "" : props.value;
        }
        if(value && props.max && (props.max as number) < value){
            value = props.value;
        }
        input.target.value = value;
    }

    const validateIntegerInputNoNegatives = (input: any) => {
        // Remove non-integer characters
        let value = input.target.value;
        if (value.startsWith('-')) {
            value = "";
        }
        else if (value) {
            value = value.replace(/[^0-9]/g, '');
        }
        if(value && (props.min || props.min === 0) && (props.min as number) > (value as number)){
            value = props.value === undefined ? "" : props.value;
        }
        if(value && props.max && (props.max as number) < value){
            value = props.value;
        }
        input.target.value = value;
    }

    const validateIntegerWithDecimals = (input: any) => {
        let value = input.target.value;
        let decimalPlaces = props.decimalPlaces ? props.decimalPlaces : 2;
        let regexpTemplate = `^-?\\d*(\\.\\d{0,${decimalPlaces}})?$`;
        let regex = new RegExp(regexpTemplate);
        if (value && (!regex.test(value) || value === '.')) {
            value = value.slice(0, -1);
        }
        if(value && (props.min || props.min === 0) && (props.min as number) > value){
            value = props.value;
        }
        if(value && props.max && (props.max as number) < value){
            value = props.value;
        }
        input.target.value = value;
    }

    const validateIntegerWithDecimalsOnBlur = (input: any) => {
        let value = input.target.value;
        if (value === '-' || value === '.') {
            input.target.value = "";
        }
    }

    var inputProps: inputProps = {
        placeholder: props.inputPlaceholder,
        className: `${inputState} ${props.inputSharedProps?.className !== undefined ? props.inputSharedProps?.className : ''}`,
        style: {
            width: '100%',
            textAlign: 'left',
            ...props.inputSharedProps?.style
        },
        type: props.type,
        value: props.value,
        disabled: props.searching || props.disabled,
        onChange: props.debounceOff ? onChangeInput : UseDebounce(onChangeInput, 500),
        max: props.max,
        min: props.min,
        step: props.step,
        pattern: props.pattern,
        onInput: props.onlyDigits ? (props.allowNegatives ? validateIntegerInputWithNegatives : validateIntegerInputNoNegatives) : props.withDecimals ? validateIntegerWithDecimals : undefined,
        onBlur: props.withDecimals || props.onlyDigits ? validateIntegerWithDecimalsOnBlur : undefined
    }

    var inputCustomizedProps: inputCustomizedProps = {
        className: props.className,
        props: inputProps,
        additionalIcon: props.additionalIcon,
        classNameIcon: inputStateType.iconValid,
        displayIcon: ValidIcon,
        searching: props.searching,
        textarea: props.hasTextarea,
        addCommentBtn: props.addCommentBtn,
        inputRef: props.inputRef,
        onClickFilterComments: props.setCommentsFilter,
        isMarkedForCommentFilter: props.isMarkedForCommentFilter,
        autoFocus: props.autoFocus,
        onKeyDown: props.onKeyDown
    }

    var labelProps: labelProps = {
        style: {
            marginBottom: '10px',
            display: "inline-block"
        }
    }

    var tipProps: TipProps = {
        style: {
            display: errorCautionDisplay
        }
    }

    return (
        <Wrapper style={stylization} children={
            <>
                <Label {...labelProps} className='body-txt-medium' labelname={props.labelName} />
                <Input {...inputCustomizedProps} />
                <ErrorTip props={tipProps} tipText={errorText} tipType={inputState === inputStateType.inputError ? 'errorTip' : undefined} additionalIcon={inputState === inputStateType.inputWarning ? emergencyHome : cancel}></ErrorTip>
            </>
        } />)
}

export default FormField;